import { motion } from 'framer-motion'
import { Skeleton } from './Skeleton'

interface PageLoaderProps {
  variant?: 'default' | 'minimal' | 'full'
}

/**
 * Animated page loader for lazy-loaded routes
 * Uses Jellyfish aesthetic with pulsing glow effects
 */
export function PageLoader({ variant = 'default' }: PageLoaderProps) {
  if (variant === 'minimal') {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <JellyfishSpinner />
      </div>
    )
  }

  if (variant === 'full') {
    return (
      <div className="fixed inset-0 bg-primary-deep/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <JellyfishSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Page header skeleton */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32 rounded-xl" />
      </div>

      {/* Content skeleton */}
      <div className="bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-2xl p-6">
        <JellyfishSpinner />
        <div className="mt-6 space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      </div>
    </div>
  )
}

interface JellyfishSpinnerProps {
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Jellyfish-inspired loading spinner with pulsing glow
 */
export function JellyfishSpinner({ size = 'md' }: JellyfishSpinnerProps) {
  const sizes = {
    sm: { container: 'w-8 h-8', dot: 'w-2 h-2' },
    md: { container: 'w-16 h-16', dot: 'w-3 h-3' },
    lg: { container: 'w-24 h-24', dot: 'w-4 h-4' },
  }

  const { container, dot } = sizes[size]

  return (
    <div className={`relative ${container} mx-auto`}>
      {/* Center glow */}
      <motion.div
        className="absolute inset-0 rounded-full bg-primary-accent/30"
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Orbiting dots */}
      {[0, 1, 2, 3].map((index) => (
        <motion.div
          key={index}
          className={`absolute ${dot} rounded-full bg-primary-accent`}
          style={{
            top: '50%',
            left: '50%',
            marginTop: `-${parseInt(dot.split('-')[1]) / 2}px`,
            marginLeft: `-${parseInt(dot.split('-')[1]) / 2}px`,
          }}
          animate={{
            x: [0, 20, 0, -20, 0],
            y: [-20, 0, 20, 0, -20],
            scale: [1, 1.2, 1, 0.8, 1],
            opacity: [1, 0.7, 1, 0.7, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: index * 0.5,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Center dot */}
      <motion.div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${dot} rounded-full bg-primary-glow`}
        animate={{
          scale: [1, 1.3, 1],
          boxShadow: [
            '0 0 10px rgba(123, 97, 255, 0.5)',
            '0 0 20px rgba(123, 97, 255, 0.8)',
            '0 0 10px rgba(123, 97, 255, 0.5)',
          ],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </div>
  )
}

/**
 * Simple text loader with animated dots
 */
export function TextLoader({ text = 'Loading' }: { text?: string }) {
  return (
    <div className="flex items-center gap-1 text-neutral-400">
      <span>{text}</span>
      <motion.span
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, times: [0, 0.5, 1] }}
      >
        .
      </motion.span>
      <motion.span
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, times: [0, 0.5, 1], delay: 0.2 }}
      >
        .
      </motion.span>
      <motion.span
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, times: [0, 0.5, 1], delay: 0.4 }}
      >
        .
      </motion.span>
    </div>
  )
}
